import moment from 'moment';
import { getByPath } from './objectUtils';

/*
 * The allowed cover start window, as a per-product spec. Nothing here knows about
 * a particular client: the product says how far it may backdate, how far forward
 * it may start, and which risk-data flag grants the backdating.
 *
 * Shared by the schema (datepicker bounds) and the `cover_start_backdate` rule in
 * vueApp.ts. Build the rule string with `coverStartRule` so both sides come from
 * one declaration.
 */

export type CoverStartWindowSpec = {
    /** Dot path in the risk data that grants backdating when strictly true. Omit to never backdate. */
    backdateWhen?: string;
    /** Days before today a backdating-eligible quote may start. Default 0. */
    backdateDays?: number;
    /** Days after today any quote may start. Default 30. */
    forwardDays?: number;
};

export const DEFAULT_FORWARD_DAYS = 30;

// The flag is written to both AdditionalFormData and the model root by some
// products, so a bare key is checked in both places.
export function allowsBackdating(riskData: any, spec: CoverStartWindowSpec): boolean {
    const path = spec.backdateWhen;
    if (!path) return false;
    if (getByPath(riskData, path) === true) return true;
    const leaf = path.split('.').pop();
    return !!leaf && getByPath(riskData, leaf) === true;
}

export function coverStartBounds(spec: CoverStartWindowSpec, allowBackdate: boolean) {
    const today = moment().startOf('day');
    return {
        min: allowBackdate ? today.clone().subtract(spec.backdateDays ?? 0, 'days') : today.clone(),
        max: today.clone().add(spec.forwardDays ?? DEFAULT_FORWARD_DAYS, 'days'),
    };
}

// Resolve the real bounds for a quote.
export function coverStartBoundsFor(riskData: any, spec: CoverStartWindowSpec) {
    return coverStartBounds(spec, allowsBackdating(riskData, spec));
}

/*
 * Vueform reads `min`/`max` as plain props, so they can't be functions. Defining
 * them as getters defers evaluation until the element mounts, which is after the
 * step that captures the flag - so the datepicker offers the right range. Apply
 * this after any schema clone, or the getter is flattened to a stale value.
 */
export function applyCoverStartBounds(
    element: any,
    spec: CoverStartWindowSpec,
    getRiskData: () => any,
    format = 'YYYY-MM-DD',
) {
    if (!element) return;
    const bounds = () => coverStartBoundsFor(getRiskData(), spec);
    Object.defineProperties(element, {
        min: { get: () => bounds().min.format(format), enumerable: true, configurable: true },
        max: { get: () => bounds().max.format(format), enumerable: true, configurable: true },
    });
}

export const COVER_START_RULE = 'cover_start_backdate';

// Renders the spec as the rule's arguments, so the schema declares it once.
export function coverStartRule(spec: CoverStartWindowSpec): string {
    return `${COVER_START_RULE}:${spec.backdateWhen ?? ''},${spec.backdateDays ?? 0},${spec.forwardDays ?? DEFAULT_FORWARD_DAYS}`;
}

// Parses those arguments back out inside the rule.
export function coverStartSpecFromArgs(args: any[]): CoverStartWindowSpec {
    const num = (value: any, fallback: number) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };
    return {
        backdateWhen: args?.[0] ? String(args[0]) : undefined,
        backdateDays: num(args?.[1], 0),
        forwardDays: num(args?.[2], DEFAULT_FORWARD_DAYS),
    };
}
